import React, { useState } from "react";
import styles from "../styles/SignInHelpForm.module.css";
import { sendSignInHelpForm } from "../helpers/apiHelper";
import { useTranslation } from "../context/TranslationContext";

// Simple email validator (intentionally forgiving)
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const ISSUE_KEYS = [
  "NOT_RECEIVING_CODE",
  "CODE_INVALID_OR_EXPIRED",
  "FACE_ID_NOT_WORKING",
  "FORGOT_MY_PASSWORD",
  "PASSWORD_NOT_WORKING",
  "UNABLE_TO_CHANGE_MY_PASSWORD",
  "OTHER",
];

export const OAB_STATES = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

export const OAB_NUMBER_MAX_LENGTH = 10;

const normaliseOabNumber = (value) =>
  String(value ?? "").replace(/\D/g, "").slice(0, OAB_NUMBER_MAX_LENGTH);

// Both OAB fields are optional; the API rejects anything outside these rules with a bare 400.
const isOabNumberValid = (value) => value === "" || /^\d{1,10}$/.test(value);
const isOabStateValid = (value) => value === "" || OAB_STATES.includes(value);

const SignInHelpForm = ({ initialEmail = "", onClose }) => {
  const [email, setEmail] = useState(initialEmail);
  const [issueType, setIssueType] = useState("");
  const [oabNumber, setOabNumber] = useState("");
  const [oabState, setOabState] = useState("");
  const [detail, setDetail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const { t, language } = useTranslation();

  const emailIsValid = isValidEmail(email);
  const issueSelected = Boolean(issueType);
  const detailsProvided = detail.trim().length > 0;
  const oabIsValid = isOabNumberValid(oabNumber) && isOabStateValid(oabState);

  const isFormValid = emailIsValid && issueSelected && detailsProvided && oabIsValid && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!emailIsValid || !issueSelected || !detailsProvided) {
      setError("signInHelpForm.errorRequired");
      return;
    }

    if (!oabIsValid) {
      setError("signInHelpForm.errorInvalid");
      return;
    }

    setLoading(true);

    try {
      const response = await sendSignInHelpForm({
        email,
        issueType,
        detail,
        submittedAt: new Date().toISOString(),
        language,
        oabNumber: oabNumber.trim() || null,
        oabState: oabState.trim().toUpperCase() || null,
      });

      if (response.success) {
        setSubmitted(true);
      } else {
        setError("signInHelpForm.errorGeneric");
      }
    } catch (err) {
      setError(err?.status === 400 ? "signInHelpForm.errorInvalid" : "signInHelpForm.errorSubmit");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={styles.confirmation}>
        <h3>{t("signInHelpForm.sentTitle")}</h3>
        <p>{t("signInHelpForm.sentCopy")}</p>

        <button type="button" className={styles.submitButton} onClick={onClose}>
          {t("signInHelpForm.closeButton")}
        </button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <h3 className={styles.heading}>{t("signInHelpForm.title")}</h3>
      <p className={styles.subTitle}>{t("signInHelpForm.subTitleCode")}</p>

      {error && <p className={styles.error}>{t(error)}</p>}

      {/* Email */}
      <label className={styles.label}>
        {t("signInHelpForm.email")}
        <input
          type="email"
          className={`${styles.input} ${email && !emailIsValid ? styles.invalid : ""}`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>

      {/* Issue type */}
      <label className={styles.label}>
        {t("signInHelpForm.issueType")}
        <select className={styles.select} value={issueType} onChange={(e) => setIssueType(e.target.value)}>
          <option value="" disabled hidden>
            {t("signInHelpForm.issuePlaceholder")}
          </option>

          {ISSUE_KEYS.map((key) => (
            <option key={key} value={key}>
              {t(`signInHelpForm.issues.${key}`)}
            </option>
          ))}
        </select>
      </label>

      {/* OAB (optional) */}
      <div className={styles.row}>
        <label className={`${styles.label} ${styles.grow}`}>
          {t("signInHelpForm.oabNumber")}
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            maxLength={OAB_NUMBER_MAX_LENGTH}
            className={styles.input}
            value={oabNumber}
            placeholder={t("signInHelpForm.oabNumberPlaceholder")}
            onChange={(e) => setOabNumber(normaliseOabNumber(e.target.value))}
          />
        </label>

        <label className={styles.label}>
          {t("signInHelpForm.oabState")}
          <select
            className={styles.select}
            value={oabState}
            onChange={(e) => setOabState(e.target.value)}
          >
            <option value="">{t("signInHelpForm.oabStatePlaceholder")}</option>
            {OAB_STATES.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Details */}
      <label className={styles.label}>
        {t("signInHelpForm.details")}
        <textarea
          className={styles.textarea}
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          rows={4}
          placeholder={t("signInHelpForm.placeholder")}
        />
      </label>

      <button type="submit" className={styles.submitButton} disabled={!isFormValid}>
        {loading ? t("signInHelpForm.sending") : t("signInHelpForm.button")}
      </button>
    </form>
  );
};

export default SignInHelpForm;
